// WHAT: Custom hook for workspace creation operations
// WHY:  Encapsulates workspace creation logic with error handling
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: Tauri core API, workspace commands
// RISKS:
//   - WARN: No validation of workspace name format or uniqueness
//   - NOTE: Workspace name is trimmed but not validated for special characters
//   - WARN: Error handling may not provide specific failure reasons

import { core } from "@tauri-apps/api";
import { useCallback } from "react";

/**
 * WHAT:
 *   Custom hook for managing workspace creation.
 *
 * WHY:
 *   Provides centralized workspace creation logic with proper error handling.
 *
 * PARAMETERS:
 *   - setError: Function to set error messages
 *   - setSuccess: Function to set success messages
 *   - setSaving: Function to set saving state
 *   - onSuccess: Callback to execute after successful creation
 *
 * RETURNS:
 *   Function to create a new workspace.
 *
 * @returns {object} Object with `addWorkspace` function
 *
 * @param setError
 *
 * @param setSuccess
 *
 * @param setSaving
 *
 * @param onSuccess
 *
 */
export function useWorkspaceCreation(
  setError: (error: null | string) => void,
  setSuccess: (success: null | string) => void,
  setSaving: (saving: boolean) => void,
  onSuccess?: () => void,
) {
  /**
   * WHAT:
   *   Creates a new workspace in the database and refreshes configuration.
   *
   * WHY:
   *   Allows users to add new workspaces for organizing time tracking aspects.
   *
   * PARAMETERS:
   *   - workspaceName: Name for the new workspace
   *
   * SIDE EFFECTS:
   *   - Creates workspace in database via backend command
   *   - Displays success/error messages
   *   - Executes onSuccess callback if provided
   *
   * RISKS:
   *   - WARN: No validation of workspace name format or uniqueness
   *   - NOTE: Workspace name is trimmed but not validated for special characters
   *   - WARN: Error handling may not provide specific failure reasons
   */
  const addWorkspace = useCallback(
    async (workspaceName: string) => {
      if (!workspaceName.trim()) {
        setError("Workspace name is required");
        return;
      }

      try {
        setSaving(true);
        setError(null);
        setSuccess(null);

        await core.invoke("add_workspace_cmd", { name: workspaceName.trim() });
        setSuccess(`✅ Workspace "${workspaceName}" created successfully!`);
        onSuccess?.();
      } catch (error) {
        const message = error instanceof Error ? error.message : "❌ Error creating workspace";
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [setError, setSuccess, setSaving, onSuccess],
  );

  return { addWorkspace };
}
